import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReferralUIData, ReferralStatus, RewardStatus } from "@/lib/schema";
import { 
  TableColumn, 
  TableCellAvatar, 
  TableCellBadge, 
  TableCellText, 
  TableCellDate, 
  TableCellAmount 
} from "./ReferralTable";
import { RewardStatusBadge } from "@/components/rewards/RewardStatusBadge";
import { Clock, CheckCircle, DollarSign } from "lucide-react";

// Referral table configuration
export const getReferralColumns = (): TableColumn<ReferralUIData>[] => [
  {
    key: "candidate",
    header: "Candidate",
    render: (referral) => (
      <TableCellAvatar
        src=""
        fallback={getInitials(referral.candidateName)}
        name={referral.candidateName}
      />
    )
  },
  {
    key: "position",
    header: "Position",
    render: (referral) => (
      <div>
        <TableCellText text={referral.jobTitle} className="text-sm font-medium text-slate-900 space-grotesk" />
        {referral.jobCategory && (
          <TableCellText text={referral.jobCategory} className="text-sm text-slate-500 space-grotesk" />
        )}
      </div>
    )
  },
  {
    key: "status",
    header: "Status",
    render: (referral) => (
      <TableCellBadge
        text={getStatusText(referral.status)}
        className={getStatusColor(referral.status)}
      />
    )
  },
  {
    key: "date",
    header: "Date",
    render: (referral) => (
      <TableCellDate date={referral.referralDate} />
    )
  },
  {
    key: "reward",
    header: "Reward",
    render: (referral) => (
      <TableCellAmount
        amount={referral.reward || "-"}
        isHighlighted={referral.status === ReferralStatus.HIRED}
      />
    )
  }
];

// Reward table configuration
export interface RewardData {
  id: string;
  candidateName: string;
  jobTitle: string;
  amount: string;
  status: RewardStatus;
  date: string;
  avatar?: string;
  referrerName?: string;
  referrerId?: string;
}

export const getRewardColumns = (onRedeemClick?: (reward: RewardData) => void): TableColumn<RewardData>[] => [
  {
    key: "candidate",
    header: "Candidate Name",
    render: (reward) => (
      <TableCellAvatar
        src={reward.avatar}
        fallback={getInitials(reward.candidateName)}
        name={reward.candidateName}
      />
    )
  },
  {
    key: "jobTitle",
    header: "Job Title",
    render: (reward) => (
      <TableCellText text={reward.jobTitle} />
    )
  },
  {
    key: "amount",
    header: "Amount",
    render: (reward) => (
      <TableCellAmount amount={reward.amount} className="font-semibold" />
    )
  },
  {
    key: "status",
    header: "Status",
    render: (reward) => <RewardStatusBadge status={reward.status} />
  },
  {
    key: "date",
    header: "Date",
    render: (reward) => (
      <TableCellDate date={reward.date} />
    )
  },
  {
    key: "actions",
    header: "Actions",
    headerClassName: "text-center",
    render: (reward, index) => {
      if (reward.status === RewardStatus.ELIGIBLE) {
        return (
          <div className="flex justify-center">
            <Button 
              variant="secondary"
              size="sm" 
              className="space-grotesk font-medium text-xs"
              onClick={() => onRedeemClick?.(reward)}
              data-testid={`redeem-button-${index}`}
            >
              REDEEM NOW
            </Button>
          </div>
        );
      } else if (reward.status === RewardStatus.PENDING) {
        return (
          <div className="flex justify-center">
            <div 
              className="inline-flex h-9 items-center justify-center rounded-md px-3 text-xs font-medium border border-[#F4C542] bg-[#FFF6DE] text-[#B45309] space-grotesk font-semibold leading-[140%] tracking-[0.36px] uppercase gap-1 cursor-not-allowed"
              data-testid={`pending-button-${index}`}
            >
              PENDING
            </div>
          </div>
        );
      } else {
        return (
          <div className="flex justify-center">
            <div 
              className="inline-flex h-9 items-center justify-center rounded-md px-3 text-xs font-medium border border-[#EAEAEA] bg-[#E7E7E7] text-[#B0B0B0] space-grotesk font-bold leading-[140%] tracking-[0.36px] uppercase cursor-not-allowed"
              data-testid={`redeemed-button-${index}`}
            >
              REDEEMED
            </div>
          </div>
        );
      }
    }
  }
];

// Admin reward table configuration (includes referrer information)
export const getAdminRewardColumns = (): TableColumn<RewardData>[] => [
  {
    key: "candidate",
    header: "Candidate Name",
    render: (reward) => (
      <TableCellAvatar
        src={reward.avatar}
        fallback={getInitials(reward.candidateName)}
        name={reward.candidateName}
      />
    )
  },
  {
    key: "referrer",
    header: "Referrer",
    render: (reward) => (
      <TableCellText text={reward.referrerName || 'Unknown Referrer'} />
    )
  },
  {
    key: "jobTitle",
    header: "Job Title",
    render: (reward) => (
      <TableCellText text={reward.jobTitle} />
    )
  },
  {
    key: "amount",
    header: "Amount",
    render: (reward) => (
      <TableCellAmount amount={reward.amount} className="font-semibold" />
    )
  },
  {
    key: "status",
    header: "Status",
    render: (reward) => <RewardStatusBadge status={reward.status} />
  },
  {
    key: "date",
    header: "Date",
    render: (reward) => (
      <TableCellDate date={reward.date} />
    )
  }
];

// Helper functions
const getStatusColor = (status: ReferralStatus) => {
  switch (status) {
    case ReferralStatus.PENDING:
      return "bg-gray-100 text-gray-800 border-gray-200";
    case ReferralStatus.SUBMITTED:
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case ReferralStatus.UNDER_REVIEW:
      return "bg-orange-100 text-orange-800 border-orange-200";
    case ReferralStatus.SHORTLISTED:
      return "bg-blue-100 text-blue-800 border-blue-200";
    case ReferralStatus.INTERVIEWED:
      return "bg-purple-100 text-purple-800 border-purple-200";
    case ReferralStatus.HIRED:
      return "bg-green-100 text-green-800 border-green-200";
    case ReferralStatus.REJECTED:
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-slate-100 text-slate-800 border-slate-200";
  }
};

const getStatusText = (status: ReferralStatus) => {
  switch (status) {
    case ReferralStatus.PENDING:
      return "Pending";
    case ReferralStatus.SUBMITTED:
      return "Submitted";
    case ReferralStatus.UNDER_REVIEW:
      return "In Review";
    case ReferralStatus.SHORTLISTED:
      return "Shortlisted";
    case ReferralStatus.INTERVIEWED:
      return "Interviewed";
    case ReferralStatus.HIRED:
      return "Hired";
    case ReferralStatus.REJECTED:
      return "Rejected";
    default:
      return status;
  }
};


const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

